/**
 * BoundaryPlateCover — engraved line-art title page ("Plate I — Site &
 * Infrastructure"). The parcel boundary, utility lines (interstate / gas
 * pipeline), north arrow, scale bar, and a category legend sit in a framed
 * plate; the report title + byline drop into a band below.
 *
 * REUSE (NO FORK): the plate art is drawn by the SHARED, KEYLESS
 * `HeroCoverBoundaryOverlay` — the same pure-SVG component the hero cover
 * uses. It reads the UNCHANGED SiteLayer[] / SiteBoundaryParcel[] /
 * boundary / tag data off the report + branding. No Google Maps key is
 * required; when a site carries no geometry the overlay returns null and we
 * fall back to a labelled empty plate (still keyless, never blank-throwing).
 *
 * Theme-aware: the plate frame, legend, north arrow, and title band read the
 * active report theme tokens (primaryColor / headingColor / headingFont /
 * bodyTextColor). Selectable on ANY theme (never a per-theme default).
 */
import React from 'react';
import { HeroCoverBoundaryOverlay } from './HeroCoverBoundaryOverlay';
import { colorForCategory } from '../../../../config/siteLayerColors';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';
import {
  resolveCoverTitle,
  resolveSiteWordmark,
  resolveBylineParts,
} from '../../../../lib/reportRendering/journalCoverData';
import { computeScaleBar } from './coverScaleBar';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import type { CoverProps } from './types';
import type { SiteLayer } from '../../../../types/site';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/** Engraver's scale-bar width, px. Shared by the segment row + tick row
 *  so the per-tick `left:%` positions land on the segment boundaries. */
const SCALE_BAR_WIDTH_PX = 96;

const UTILITY_LINE_HINT = /transmission|pipeline|pipe|kV|electric|natural gas|water main|sewer|fiber|interstate|highway|road/i;

function isLineLayer(layer: Pick<SiteLayer, 'name' | 'category' | 'geometry'>): boolean {
  if (layer.geometry.type !== 'line') return false;
  return true;
}

export const BoundaryPlateCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
}) => {
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };

  const title = resolveCoverTitle(report);
  // COVER-CONTENT BATCH (1/2): the title-band kicker is the SITE NAME, not the
  // "{ORG} · BTM Feasibility" org kicker. Org identity stays in the byline.
  const kicker = resolveSiteWordmark(report, branding);
  const bylineParts = resolveBylineParts(report, branding);

  const layers = effectiveBranding.kmzLayers ?? [];
  const hasBoundary =
    (report.siteBoundaries?.length ?? 0) > 0 || (report.siteBoundary?.length ?? 0) > 0;
  const hasLayers = layers.length > 0;
  const hasGeometry = hasBoundary || hasLayers;

  // Parcel-sized scale bar (segments + tick labels kept in sync). Only
  // meaningful when the site itself has a drawn boundary — the helper
  // measures the parcel extent, not the loose KMZ layers.
  const scaleBar = hasBoundary ? computeScaleBar(report) : null;

  // Category legend — parcel + each distinct utility line / asset category.
  // Reads the SAME colour mapping the overlay draws with (colorForCategory),
  // so the legend swatches match the drawn art.
  const legendEntries: Array<{ id: string; label: string; color: string; line: boolean }> = [];
  legendEntries.push({ id: 'parcel', label: 'Parcel boundary', color: theme.primaryColor, line: false });
  const seenCategories = new Set<string>();
  for (const layer of layers) {
    const key = `${layer.category}`;
    if (seenCategories.has(key)) continue;
    seenCategories.add(key);
    legendEntries.push({
      id: layer.id,
      label: layer.name,
      color: colorForCategory(layer.category, layer.color),
      line: isLineLayer(layer),
    });
    if (legendEntries.length >= 5) break;
  }

  return (
    <div
      className="relative flex flex-col"
      data-testid="cover-boundary-plate"
      data-cover-variant="boundary-plate"
      style={{ color: roleColor(theme, 'body'), minHeight: COVER_MIN_HEIGHT_PX }}
    >
      {/* ── Plate: engraved line-art on a faint surface, framed by a strong
            bottom rule in the heading colour. ── */}
      <div
        className="relative flex-1"
        data-testid="cover-boundary-plate-art"
        style={{
          minHeight: 320,
          backgroundColor: theme.backgroundColor,
          borderBottom: `2px solid ${reportHeadingInk(theme)}`,
          backgroundImage:
            `linear-gradient(${roleColorAlpha(theme, 'ink', 0.067)} 1px, transparent 1px),` +
            `linear-gradient(90deg, ${roleColorAlpha(theme, 'ink', 0.067)} 1px, transparent 1px)`,
          backgroundSize: '34px 34px',
        }}
      >
        {/* Plate number / title — engraver's slug, top-left. */}
        <div
          className="absolute left-7 top-5 text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont }}
          data-testid="cover-boundary-plate-no"
        >
          Plate I – Site &amp; Infrastructure
        </div>

        {/* North arrow, top-right. */}
        <svg
          className="absolute right-8 top-4"
          width="28"
          height="44"
          viewBox="0 0 28 44"
          aria-hidden="true"
          data-testid="cover-boundary-plate-north"
        >
          <line x1="14" y1="30" x2="14" y2="6" stroke={reportHeadingInk(theme)} strokeWidth="1.4" />
          <polygon points="14,2 9,12 19,12" fill={reportHeadingInk(theme)} />
          <text x="14" y="42" textAnchor="middle" fontSize="11" fontFamily={theme.bodyFont} fill={reportHeadingInk(theme)}>N</text>
        </svg>

        {/* The keyless SVG boundary art (shared overlay) or an empty-plate
            fallback when the site has no geometry. */}
        <div className="absolute inset-0 flex items-center justify-center p-12 pointer-events-none">
          {hasGeometry ? (
            <HeroCoverBoundaryOverlay
              report={report}
              layers={layers}
              layerLabels={effectiveBranding.layerIndicators}
              textTone="on-light"
              strokeColor={theme.primaryColor}
              className="h-full w-full max-w-none aspect-auto"
            />
          ) : (
            <div
              className="text-[11px] uppercase tracking-[0.2em]"
              style={{ color: roleColor(theme, 'muted') }}
              data-testid="cover-boundary-plate-empty"
            >
              Site geometry not available
            </div>
          )}
        </div>

        {/* Category legend, top-right under the north arrow.
            N1.5C-flat — in editorial mode the legend is an OPEN caption: drop the
            box (no `border`, no `theme.backgroundColor` fill) so the colour-dot
            rows read as an open key on the cream plate. Flag OFF keeps the framed
            box (border + bg) byte-identical. */}
        <div
          className={
            isEditorialLayoutsEnabled()
              ? 'absolute right-6 top-14 px-3 py-2 text-[9.5px]'
              : 'absolute right-6 top-14 border px-3 py-2 text-[9.5px]'
          }
          style={
            isEditorialLayoutsEnabled()
              ? { background: 'transparent', fontFamily: theme.bodyFont }
              : {
                  backgroundColor: theme.backgroundColor,
                  borderColor: roleColorAlpha(theme, 'ink', 0.33),
                  fontFamily: theme.bodyFont,
                }
          }
          data-testid="cover-boundary-plate-legend"
        >
          {legendEntries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-2 mb-1 last:mb-0" style={{ color: roleColor(theme, 'body') }}>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: entry.line ? 0 : 3,
                  borderTop: entry.line ? `2px ${entry.id === 'parcel' ? 'solid' : 'dashed'} ${entry.color}` : undefined,
                  background: entry.line ? undefined : entry.color,
                }}
              />
              <span className="truncate max-w-[120px]">{entry.label}</span>
            </div>
          ))}
        </div>

        {/* Scale bar, bottom-left — derived from the TRUE parcel extent so
            the bar, its segments, and EVERY tick label stay in sync (the
            segment-draw loop and the tick-label loop both read the same
            `scaleBar.ticks` model). Hidden when the site has no geometry,
            since a scale on an empty plate is meaningless. */}
        {hasBoundary && scaleBar && (
          <div
            className="absolute left-7 bottom-6 text-[9px]"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont }}
            data-testid="cover-boundary-plate-scalebar"
          >
            <div
              className="flex"
              style={{ width: SCALE_BAR_WIDTH_PX, height: 5, border: `1px solid ${reportHeadingInk(theme)}`, borderTop: 0 }}
            >
              {Array.from({ length: scaleBar.segmentCount }).map((_, i) => (
                <span
                  key={`seg-${i}`}
                  style={{
                    flex: 1,
                    // Alternate filled / empty; never paint past the last tick.
                    background: i % 2 === 1 ? reportHeadingInk(theme) : undefined,
                    borderRight:
                      i < scaleBar.segmentCount - 1 ? `1px solid ${reportHeadingInk(theme)}` : undefined,
                  }}
                />
              ))}
            </div>
            {/* One label per tick (0 … total), positioned at each tick. */}
            <div className="relative" style={{ width: SCALE_BAR_WIDTH_PX, height: 12, marginTop: 1 }}>
              {scaleBar.ticks.map((tick) => {
                const pct = (tick.index / scaleBar.segmentCount) * 100;
                const anchor =
                  tick.index === 0 ? 'left' : tick.index === scaleBar.segmentCount ? 'right' : 'center';
                return (
                  <span
                    key={`tick-${tick.index}`}
                    className="absolute top-0 whitespace-nowrap"
                    style={{
                      left: `${pct}%`,
                      transform:
                        anchor === 'left'
                          ? 'translateX(0)'
                          : anchor === 'right'
                            ? 'translateX(-100%)'
                            : 'translateX(-50%)',
                    }}
                    data-testid={`cover-boundary-plate-scalebar-tick-${tick.index}`}
                  >
                    {tick.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Title band below the plate. ── */}
      <div className="px-12 pt-7 pb-9">
        <div
          className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2"
          style={{ color: theme.primaryColor, fontFamily: theme.bodyFont }}
          data-testid="cover-site-name-header"
        >
          {kicker}
        </div>
        <h1
          className="leading-tight tracking-tight"
          id="cover-title"
          // Title size keys off the resolved headingScale (editorial >
          // technical > compact) instead of a fixed clamp; the responsive
          // band is bounded by the resolved h2 size. Display font falls back
          // to headingFont.
          style={{
            fontFamily: theme.displayFont || theme.headingFont,
            color: reportHeadingInk(theme),
            fontSize: `clamp(28px, 4.4vw, calc(${reportTypographySizes(theme).h2} * 1.83))`,
            fontWeight: 500,
          }}
          data-testid="cover-boundary-plate-title"
        >
          {title}
        </h1>
        {bylineParts.length > 0 && (
          <div
            className="text-[11px] mt-3 uppercase tracking-[0.05em]"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.85 }}
            data-testid="cover-boundary-plate-byline"
          >
            {bylineParts.join(' · ')}
          </div>
        )}
      </div>

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (COVER-CONTENT BATCH 4),
          rendered at 80% of the prior 10px size (→ 8px). */}
      <div
        className="absolute bottom-0 right-0 px-12 pb-4 text-right text-[8px] leading-relaxed max-w-md"
        style={{ color: roleColor(theme, 'body'), opacity: 0.8 }}
        data-testid="cover-boundary-plate-disclaimer"
      >
        {disclaimer}
      </div>

      {/* GATEWAY POWER platform credit — just BELOW the cover box on the title
          band's px-12 gutter (absolute, top: 100% + 3px): free at every width,
          zero height, clear of the bottom-right disclaimer however it wraps. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        className="absolute left-12 top-[calc(100%_+_3px)]"
        testId="cover-boundary-plate-platform-credit"
      />
    </div>
  );
};
